<template>
  <div>
    <div>
      <input ref="autocompleteInput" v-model="address" class="form-control mb-2"
        placeholder="Escribe la dirección y selecciona una sugerencia" autocomplete="off"
        @keydown.enter.prevent="onEnter" />
    </div>
    <div :id="mapId" style="width: 100%; height: 300px;"></div>
    <div v-if="coords" class="mt-2">
      <small>Coordenadas: {{ coords.lat }}, {{ coords.lng }}</small>
    </div>
    <div v-if="desglosado">
      <div class="mt-2">
        <strong>Dirección desglosada:</strong>
        <ul class="mb-0">
          <li><b>Calle y número:</b> {{ desglosado.street }}</li>
          <li><b>Colonia:</b> {{ desglosado.neighborhood }}</li>
          <li><b>Ciudad:</b> {{ desglosado.city }}</li>
          <li><b>Estado:</b> {{ desglosado.state }}</li>
          <li><b>Código postal:</b> {{ desglosado.postal_code }}</li>
          <li><b>País:</b> {{ desglosado.country }}</li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script>
function extractAddressComponents(components) {
  const get = (type) => {
    const comp = components.find(c => c.types.includes(type));
    return comp ? comp.long_name : '';
  };
  return {
    street: [get('route'), get('street_number')].filter(Boolean).join(' '),
    neighborhood: get('sublocality') || get('neighborhood') || get('political'),
    city: get('locality') || get('administrative_area_level_2'),
    state: get('administrative_area_level_1'),
    postal_code: get('postal_code'),
    country: get('country'),
  };
}

export default {
  props: {
    value: String, // coordenadas iniciales "lat,lng"
    addressValue: String, // dirección inicial
  },
  data() {
    return {
      map: null,
      marker: null,
      geocoder: null,
      autocomplete: null,
      coords: this.value ? this.parseCoords(this.value) : null,
      address: this.addressValue || '',
      mapId: 'google-map-' + Math.random().toString(36).substr(2, 9),
      autocompleteListener: null,
      desglosado: null,
    };
  },
  watch: {
    value(val) {
      if (val) {
        this.coords = this.parseCoords(val);
        this.updateMarker();
      }
    },
    addressValue(val) {
      if (val) {
        this.address = val;
      }
    },
    address(newAddress) {
      this.$emit('update:addressValue', newAddress);
    }
  },
  mounted() {
    this.loadGoogleMapsScript().then(() => {
      this.initMap();
      this.initAutocomplete();
    });
  },
  beforeDestroy() {
    if (this.autocompleteListener) {
      window.google.maps.event.removeListener(this.autocompleteListener);
    }
  },
  methods: {
    parseCoords(val) {
      if (!val) return null;
      const [lat, lng] = val.split('+').map(Number);
      return { lat, lng };
    },
    loadGoogleMapsScript() {
      return new Promise((resolve) => {
        if (window.google && window.google.maps && window.google.maps.places) {
          resolve();
          return;
        }
        const scriptId = 'google-maps-script';
        if (document.getElementById(scriptId)) {
          document.getElementById(scriptId).addEventListener('load', resolve);
          return;
        }
        const script = document.createElement('script');
        script.id = scriptId;
        script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.VUE_APP_GOOGLE_MAPS_API_KEY}&libraries=places`;
        script.async = true;
        script.defer = true;
        script.onload = resolve;
        document.head.appendChild(script);
      });
    },
    initMap() {
      this.geocoder = new window.google.maps.Geocoder();
      const center = this.coords || { lat: 28.639166666667, lng: -106.07333333333 };
      this.map = new window.google.maps.Map(document.getElementById(this.mapId), {
        center,
        zoom: 12,
      });
      this.marker = new window.google.maps.Marker({
        position: center,
        map: this.map,
        draggable: true,
      });
      this.marker.addListener('dragend', this.onMarkerDrag);
      if (this.coords) {
        this.$emit('input', `${this.coords.lat}+${this.coords.lng}`);
      }
    },
    updateMarker() {
      if (this.map && this.marker && this.coords) {
        this.map.setCenter(this.coords);
        this.marker.setPosition(this.coords);
      }
    },
    initAutocomplete() {
      if (!window.google || !window.google.maps.places) return;
      const input = this.$refs.autocompleteInput;
      this.autocomplete = new window.google.maps.places.Autocomplete(input, {
        types: ['geocode'],
        componentRestrictions: { country: 'mx' },
      });
      this.autocompleteListener = this.autocomplete.addListener('place_changed', this.onPlaceChanged);
    },
    onPlaceChanged() {
      const place = this.autocomplete.getPlace();
      if (!place.geometry) {
        this.geocodeAddress();
        return;
      }
      const loc = place.geometry.location;
      const coords = { lat: loc.lat(), lng: loc.lng() };
      this.coords = coords;
      this.map.setCenter(coords);
      this.marker.setPosition(coords);
      this.$emit('input', `${coords.lat}+${coords.lng}`);
      this.address = place.formatted_address || this.address;
      // Desglose de dirección
      this.desglosado = extractAddressComponents(place.address_components || []);
      this.$emit('address-details', this.desglosado);
    },
    onEnter() {
      // Si el usuario presiona Enter sin seleccionar sugerencia, hacer geocoding manual
      this.geocodeAddress();
    },
    geocodeAddress() {
      if (!this.address) return;
      this.geocoder.geocode({ address: this.address }, (results, status) => {
        if (status === 'OK' && results[0]) {
          const loc = results[0].geometry.location;
          const coords = { lat: loc.lat(), lng: loc.lng() };
          this.coords = coords;
          this.map.setCenter(coords);
          this.marker.setPosition(coords);
          this.$emit('input', `${coords.lat}+${coords.lng}`);
          // Desglose de dirección
          this.desglosado = extractAddressComponents(results[0].address_components || []);
          this.$emit('address-details', this.desglosado);
        } else {
          alert('No se pudo encontrar la dirección.');
        }
      });
    },
    onMarkerDrag() {
      const pos = this.marker.getPosition();
      const coords = { lat: pos.lat(), lng: pos.lng() };
      this.coords = coords;
      this.$emit('input', `${coords.lat}+${coords.lng}`);
    },
  },
};
</script>
